import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Modal,
  Spinner,
  Alert,
  Form,
  InputGroup,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faUsers,
  faClipboardList,
  faTasks,
  faClock,
  faCheckCircle,
  faCircle,
  faExclamationCircle,
  faList,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { getMeetings, getMembers, getCommittees } from "../api/api";
import { getTasks, getTask, updateTask } from "../api/tasks";
import { getProjects } from "../api/projects";
import LotusIcon from "../components/LotusIcon";
import { useAuth } from "../context/AuthContext";

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [userTasks, setUserTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Task detail modal state
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);

  // Task edit modal state
  const [showTaskEditModal, setShowTaskEditModal] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    dueDate: "",
    assignedTo: "",
    projectId: "",
    relationships: {
      project: null,
      committee: null,
      meeting: null,
    },
    labels: [],
  });
  const [newLabel, setNewLabel] = useState("");
  const [availableProjects, setAvailableProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchRecentMeetings = async () => {
      try {
        setLoading(true);
        const meetings = await getMeetings({ limit: 3, sort: "-date" });
        setRecentMeetings(meetings || []);
      } catch (error) {
        console.error("Error fetching recent meetings:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserTasks = async () => {
      try {
        setTasksLoading(true);
        const tasks = await getTasks();
        setUserTasks(tasks || []);
      } catch (error) {
        console.error("Error fetching user tasks:", error);
      } finally {
        setTasksLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const data = await getMembers();
        setUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setAvailableProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    const fetchCommittees = async () => {
      try {
        const data = await getCommittees();
        setCommittees(data);
      } catch (error) {
        console.error("Error fetching committees:", error);
      }
    };

    fetchRecentMeetings();
    fetchUserTasks();
    fetchUsers();
    fetchProjects();
    fetchCommittees();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get task priority badge
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "High":
        return (
          <Badge bg="danger" pill>
            High
          </Badge>
        );
      case "Medium":
        return (
          <Badge bg="warning" text="dark" pill>
            Medium
          </Badge>
        );
      case "Low":
        return (
          <Badge bg="info" pill>
            Low
          </Badge>
        );
      default:
        return null;
    }
  };

  // Get task status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "Done":
        return (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
        );
      case "In Progress":
        return <FontAwesomeIcon icon={faCircle} className="text-warning" />;
      case "To Do":
      default:
        return <FontAwesomeIcon icon={faCircle} className="text-secondary" />;
    }
  };

  // Format due date - standard format without warning colors
  const formatTaskDate = (dateString) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Function to get status badge color for tasks in modal
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "To Do":
        return "secondary";
      case "In Progress":
        return "info";
      case "Done":
        return "success";
      default:
        return "secondary";
    }
  };

  // Function to get priority badge color for tasks in modal
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return "danger";
      case "Medium":
        return "warning";
      case "Low":
        return "success";
      default:
        return "secondary";
    }
  };

  // Handle opening a task's details in a modal
  const handleOpenTaskDetail = async (taskId) => {
    setLoadingTaskDetails(true);
    setShowTaskDetailModal(true);

    try {
      const fetchedTask = await getTask(taskId);
      setSelectedTask(fetchedTask);
    } catch (error) {
      console.error("Error fetching task details:", error);
    } finally {
      setLoadingTaskDetails(false);
    }
  };

  // Handle editing a task
  const handleEditTask = () => {
    if (!selectedTask) return;

    // Determine relationships from either relatedTo or relationships field
    const relationships = {
      project: null,
      committee: null,
      meeting: null,
    };

    // First check relatedTo field
    if (
      selectedTask.relatedTo?.type &&
      selectedTask.relatedTo?.id &&
      selectedTask.relatedTo?.name
    ) {
      relationships[selectedTask.relatedTo.type] = {
        id: selectedTask.relatedTo.id,
        name: selectedTask.relatedTo.name,
        type: selectedTask.relatedTo.type,
      };
    }
    // Then check relationships field (if different from relatedTo)
    else if (
      selectedTask.relationships?.type &&
      selectedTask.relationships?.id &&
      selectedTask.relationships?.name
    ) {
      relationships[selectedTask.relationships.type] = {
        id: selectedTask.relationships.id,
        name: selectedTask.relationships.name,
        type: selectedTask.relationships.type,
      };
    }
    // Also handle projectId if it exists
    else if (selectedTask.projectId) {
      const project = availableProjects.find(
        (p) => p._id === selectedTask.projectId
      );
      if (project) {
        relationships.project = {
          id: selectedTask.projectId,
          name: project.title,
          type: "project",
        };
      }
    }

    setFormData({
      title: selectedTask.title || "",
      description: selectedTask.description || "",
      status: selectedTask.status || "To Do",
      priority: selectedTask.priority || "Medium",
      dueDate: selectedTask.dueDate?.split("T")[0] || "",
      assignedTo: selectedTask.assignedTo?._id || "",
      projectId: selectedTask.projectId || "",
      relationships: relationships,
      labels: selectedTask.labels || [],
      createdBy: selectedTask.createdBy,
    });

    setShowTaskDetailModal(false);
    setShowTaskEditModal(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Add label to task
  const handleAddLabel = () => {
    if (newLabel.trim() && !formData.labels.includes(newLabel.trim())) {
      setFormData({
        ...formData,
        labels: [...formData.labels, newLabel.trim()],
      });
      setNewLabel("");
    }
  };

  // Remove label from task
  const handleRemoveLabel = (labelToRemove) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter((label) => label !== labelToRemove),
    });
  };

  // Submit task updates
  const handleSubmitTask = async () => {
    try {
      // Validate required fields
      if (!formData.title) {
        setErrorMessage("Task title is required");
        return;
      }

      // Clear any previous error
      setErrorMessage("");

      // Prepare the task data with only required title field
      const taskData = {
        title: formData.title,
      };

      // Add optional fields if they have values
      if (formData.description) taskData.description = formData.description;
      if (formData.status) taskData.status = formData.status;
      if (formData.priority) taskData.priority = formData.priority;

      // Handle date formatting
      if (formData.dueDate && formData.dueDate.trim()) {
        taskData.dueDate = new Date(formData.dueDate).toISOString();
      }

      // Make sure assignedTo is only added if it's a valid value
      if (formData.assignedTo && formData.assignedTo.trim()) {
        taskData.assignedTo = formData.assignedTo;
      }

      if (formData.labels?.length > 0) taskData.labels = formData.labels;

      // Handle relationships - Find the first non-null relationship
      const activeRelationship = Object.entries(formData.relationships).find(
        ([_, data]) => data !== null
      );

      if (activeRelationship) {
        const [type, data] = activeRelationship;

        // Ensure data has all required fields
        if (data && data.id && data.name && data.type) {
          // Set the relatedTo field for server-side
          taskData.relatedTo = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // Also set the relationships field for client-side
          taskData.relationships = {
            type: data.type,
            id: data.id,
            name: data.name,
          };

          // For backwards compatibility with projectId
          if (data.type === "project") {
            taskData.projectId = data.id;
          }
        }
      }

      await updateTask(selectedTask._id, taskData);

      // Update the tasks list and selected task
      const updatedTasks = await getTasks();
      setUserTasks(updatedTasks || []);

      // Get the updated task details
      const updatedTask = await getTask(selectedTask._id);
      setSelectedTask(updatedTask);

      // Close edit modal and show detail modal with updated task
      setShowTaskEditModal(false);
      setShowTaskDetailModal(true);
    } catch (error) {
      console.error("Error updating task:", error);
      setErrorMessage("Failed to update task");
    }
  };

  // Refresh tasks after update
  const refreshTasks = async () => {
    try {
      setTasksLoading(true);
      const tasks = await getTasks();
      setUserTasks(tasks || []);
    } catch (error) {
      console.error("Error refreshing tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  return (
    <Container>
      <div className="section-title d-flex align-items-center mb-4">
        <LotusIcon className="me-3" size={32} />
        <h1 className="mb-0">Dashboard</h1>
      </div>

      <Row className="mb-4">
        <Col md={6} lg={3} className="mb-3">
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Body className="d-flex flex-column align-items-center text-center p-4">
              <div
                className="icon-circle mb-3"
                style={{
                  background: "rgba(217, 102, 155, 0.1)",
                  padding: "20px",
                  borderRadius: "50%",
                }}
              >
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{ fontSize: "2rem", color: "#d9669b" }}
                />
              </div>
              <h3 className="mb-0" style={{ color: "#d9669b" }}>
                Meetings
              </h3>
              <p className="text-muted">View and manage your meetings</p>
              <Link to="/meetings" className="mt-auto">
                <Button
                  variant="outline-primary"
                  style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                >
                  Go to Meetings
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Body className="d-flex flex-column align-items-center text-center p-4">
              <div
                className="icon-circle mb-3"
                style={{
                  background: "rgba(212, 175, 55, 0.1)",
                  padding: "20px",
                  borderRadius: "50%",
                }}
              >
                <FontAwesomeIcon
                  icon={faUsers}
                  style={{ fontSize: "2rem", color: "#d4af37" }}
                />
              </div>
              <h3 className="mb-0" style={{ color: "#d4af37" }}>
                Members
              </h3>
              <p className="text-muted">View and manage members</p>
              <Link to="/members" className="mt-auto">
                <Button
                  variant="outline-warning"
                  style={{ borderColor: "#f5d88e", color: "#d4af37" }}
                >
                  Go to Members
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Body className="d-flex flex-column align-items-center text-center p-4">
              <div
                className="icon-circle mb-3"
                style={{
                  background: "rgba(155, 102, 217, 0.1)",
                  padding: "20px",
                  borderRadius: "50%",
                }}
              >
                <FontAwesomeIcon
                  icon={faClipboardList}
                  style={{ fontSize: "2rem", color: "#9b66d9" }}
                />
              </div>
              <h3 className="mb-0" style={{ color: "#9b66d9" }}>
                Committees
              </h3>
              <p className="text-muted">View and manage committees</p>
              <Link to="/committees" className="mt-auto">
                <Button
                  variant="outline-info"
                  style={{ borderColor: "#d5c2f5", color: "#9b66d9" }}
                >
                  Go to Committees
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Body className="d-flex flex-column align-items-center text-center p-4">
              <div
                className="icon-circle mb-3"
                style={{
                  background: "rgba(76, 175, 80, 0.1)",
                  padding: "20px",
                  borderRadius: "50%",
                }}
              >
                <FontAwesomeIcon
                  icon={faTasks}
                  style={{ fontSize: "2rem", color: "#4CAF50" }}
                />
              </div>
              <h3 className="mb-0" style={{ color: "#4CAF50" }}>
                Tasks
              </h3>
              <p className="text-muted">View and manage your tasks</p>
              <Link to="/tasks" className="mt-auto">
                <Button
                  variant="outline-success"
                  style={{ borderColor: "#c2f5c4", color: "#4CAF50" }}
                >
                  Go to Tasks
                </Button>
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Header
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <h5 className="mb-0">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="me-2"
                  style={{ color: "#d9669b" }}
                />
                Recent Meetings
              </h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-3">
                  <div className="lotus-petals">
                    <LotusIcon size={32} />
                  </div>
                  <p className="mt-2">Loading recent meetings...</p>
                </div>
              ) : recentMeetings.length > 0 ? (
                <div className="list-group">
                  {recentMeetings.map((meeting) => (
                    <Link
                      key={meeting._id}
                      to={`/meetings/${meeting._id}`}
                      className="list-group-item list-group-item-action border-0 mb-2 rounded"
                      style={{ transition: "all 0.2s ease" }}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <h5 className="mb-1" style={{ color: "#d9669b" }}>
                          {meeting.title}
                        </h5>
                        <small>{formatDate(meeting.date)}</small>
                      </div>
                      <p className="mb-1 text-muted small">
                        {meeting.description?.substring(0, 120)}...
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3">
                  <p>No recent meetings found.</p>
                  <Link to="/meetings/new">
                    <Button variant="primary" size="sm">
                      Create a Meeting
                    </Button>
                  </Link>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-white text-center">
              <Link to="/meetings" className="App-link">
                View All Meetings{" "}
                <FontAwesomeIcon icon={faCalendarAlt} className="ms-1" />
              </Link>
            </Card.Footer>
          </Card>
        </Col>

        <Col md={6}>
          <Card
            className="shadow-sm border-0 h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Card.Header
              style={{
                background: "linear-gradient(to right, #e8f5e9, #f1f8e9)",
              }}
            >
              <h5 className="mb-0">
                <FontAwesomeIcon
                  icon={faTasks}
                  className="me-2"
                  style={{ color: "#4CAF50" }}
                />
                Your Tasks
              </h5>
            </Card.Header>
            <Card.Body>
              {tasksLoading ? (
                <div className="text-center py-3">
                  <div className="lotus-petals">
                    <LotusIcon size={32} />
                  </div>
                  <p className="mt-2">Loading your tasks...</p>
                </div>
              ) : userTasks.length > 0 ? (
                <div className="list-group">
                  {userTasks.slice(0, 5).map((task) => (
                    <div
                      key={task._id}
                      onClick={() => handleOpenTaskDetail(task._id)}
                      className="list-group-item list-group-item-action border-0 mb-2 rounded"
                      style={{ transition: "all 0.2s ease", cursor: "pointer" }}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {getStatusIcon(task.status)}
                          <h5
                            className="mb-0 ms-2"
                            style={{ color: "#4CAF50" }}
                          >
                            {task.title}
                          </h5>
                        </div>
                        <div>{getPriorityBadge(task.priority)}</div>
                      </div>
                      <div className="d-flex justify-content-between mt-2 align-items-center">
                        <span className="text-muted small">
                          {task.projectId?.title
                            ? `Project: ${task.projectId.title}`
                            : ""}
                        </span>
                        <span className="text-muted small">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {formatTaskDate(task.dueDate)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3">
                  <p>No tasks assigned to you.</p>
                  <Link to="/tasks">
                    <Button variant="success" size="sm">
                      Create a Task
                    </Button>
                  </Link>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-white text-center">
              <Link
                to="/tasks"
                className="App-link"
                style={{ color: "#4CAF50" }}
              >
                View All Tasks{" "}
                <FontAwesomeIcon icon={faTasks} className="ms-1" />
              </Link>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Task Detail Modal */}
      <Modal
        show={showTaskDetailModal}
        onHide={() => setShowTaskDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(to right, #fff0f5, #fff8dc)",
          }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            <FontAwesomeIcon icon={faTasks} className="me-2" />
            Task Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingTaskDetails ? (
            <div className="text-center py-3">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : !selectedTask ? (
            <Alert variant="warning">Task not found</Alert>
          ) : (
            <>
              <h4 className="mb-3" style={{ color: "#d9669b" }}>
                {selectedTask.title}
              </h4>

              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-muted">Status</h6>
                    <Badge
                      bg={getStatusBadgeColor(selectedTask.status)}
                      style={{ fontSize: "1rem", padding: "8px 12px" }}
                    >
                      {selectedTask.status}
                    </Badge>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-muted">Priority</h6>
                    <Badge
                      bg={getPriorityBadgeColor(selectedTask.priority)}
                      style={{ fontSize: "1rem", padding: "8px 12px" }}
                    >
                      {selectedTask.priority}
                    </Badge>
                  </div>
                </Col>
              </Row>

              {selectedTask.description && (
                <div className="mb-4">
                  <h6 className="text-muted">Description</h6>
                  <p>{selectedTask.description}</p>
                </div>
              )}

              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-muted">Assigned To</h6>
                    <p>{selectedTask.assignedTo?.name || "Unassigned"}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-muted">Due Date</h6>
                    <p>{formatTaskDate(selectedTask.dueDate)}</p>
                  </div>
                </Col>
              </Row>

              {selectedTask.labels && selectedTask.labels.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted">Labels</h6>
                  <div className="d-flex flex-wrap">
                    {selectedTask.labels.map((label) => (
                      <Badge
                        key={label}
                        bg="light"
                        text="dark"
                        className="me-1 mb-1"
                        style={{ border: "1px solid #f5c2c2" }}
                      >
                        # {label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {(selectedTask.relationships?.name ||
                selectedTask.relatedTo?.name) && (
                <div className="mb-4">
                  <h6 className="text-muted">Related To</h6>
                  <Badge
                    bg={
                      (selectedTask.relationships?.type ||
                        selectedTask.relatedTo?.type) === "meeting"
                        ? "info"
                        : (selectedTask.relationships?.type ||
                            selectedTask.relatedTo?.type) === "committee"
                        ? "warning"
                        : "success"
                    }
                    style={{ fontSize: "0.9rem", padding: "5px 10px" }}
                  >
                    <FontAwesomeIcon
                      icon={
                        (selectedTask.relationships?.type ||
                          selectedTask.relatedTo?.type) === "meeting"
                          ? faCalendarAlt
                          : (selectedTask.relationships?.type ||
                              selectedTask.relatedTo?.type) === "committee"
                          ? faUsers
                          : faList
                      }
                      className="me-1"
                    />
                    {selectedTask.relationships?.name ||
                      selectedTask.relatedTo?.name}
                  </Badge>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleEditTask}
            style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
            disabled={!selectedTask || loadingTaskDetails}
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Task
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowTaskDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Task Edit Modal */}
      <Modal
        show={showTaskEditModal}
        onHide={() => {
          setShowTaskEditModal(false);
          setShowTaskDetailModal(true);
        }}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "linear-gradient(to right, #fff0f5, #fff8dc)" }}
        >
          <Modal.Title style={{ color: "#d9669b" }}>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && (
            <Alert variant="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Task Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter task title"
                required
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter task description"
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #f5c2c2",
                }}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Done">Done</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assigned To</Form.Label>
                  <Form.Select
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project</Form.Label>
                  <Form.Select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleInputChange}
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f5c2c2",
                    }}
                  >
                    <option value="">None</option>
                    {availableProjects.map((project) => (
                      <option key={project._id} value={project._id}>
                        {project.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Labels</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Add a label"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddLabel();
                    }
                  }}
                  style={{
                    backgroundColor: "#fafafa",
                    border: "1px solid #f5c2c2",
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAddLabel}
                  style={{ borderColor: "#f5c2c2", color: "#d9669b" }}
                >
                  Add
                </Button>
              </InputGroup>

              {formData.labels.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {formData.labels.map((label) => (
                    <Badge
                      key={label}
                      bg="light"
                      text="dark"
                      className="mb-1 d-flex align-items-center"
                      style={{ border: "1px solid #f5c2c2" }}
                    >
                      # {label}
                      <span
                        onClick={() => handleRemoveLabel(label)}
                        style={{ cursor: "pointer", marginLeft: "5px" }}
                      >
                        &times;
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowTaskEditModal(false);
              setShowTaskDetailModal(true);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitTask}
            style={{
              background: "linear-gradient(to right, #e66b8f, #d4af37)",
              border: "none",
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;
